// Which hair atlases a player's look draws (character look plan 2, 2026-10-30). The body sheets are hairless; the
// hair is two layers of its own, the back (behind the body, beside the shoulders) and the front (over the clothes,
// under a hat). scripts/anim/hair writes them: <c>_walk_hair_<style> / <c>_walk_hairback_<style> (also the portrait
// parts), <c>_pose_hair… and bike_hair_<c>_<style> (front and back as frames of one atlas). A style worn under a
// head-covering item draws its flattened '_hat' variant in the families that have one (plan 3: only where its own hair
// pokes out, scripts/anim/hair/hat_fit.py needs_variant). The default style has a third layer, the neck (decision 15):
// today's skin its hair carried, drawn over the body and under the clothes — <c>_walk_neck, <c>_pose_neck and the
// '<view>/neck' frames of its bike atlas; another style's body has the bald head's own neck.
import { LOOK_CATALOG, resolveLook } from './lookCatalog.mjs';

/** The accessories that cover the head: a style with a hat variant draws it under these. */
export const HEAD_COVER = new Set(['akc_kapelusz', 'nat_bucket_hat', 'akc_korona', 'ken_akc_cap', 'ken_akc_beanie']);

const EVERY = { walk: true, pose: true, bike: true, driver: true, hat: [] };
/** Which families each style has art for, and the families ('walk' | 'pose' | 'bike') with an under-a-hat variant.
 *  `right`: a one-sided style's own right-facing car picture (its walk, pose and bike atlases carry right-facing frames
 *  of their own, which the game picks up where they are). */
export const HAIR_ART = {
  nat_pigtails: EVERY,
  ken_curls: EVERY,
  // plan 3's pilot batch (Task 11)
  nat_long_sleek: { ...EVERY, hat: ['walk', 'bike'] },
  nat_rounded_bob: { ...EVERY, hat: ['walk', 'bike'], right: true },   // its clip sits on her right (gate 4a)
  ken_short_crop: { ...EVERY, hat: ['bike'] },
  // batch 2 (Task 12); `hat` lists a family once its under-a-hat variant is kept and cut
  nat_wavy_lob: { ...EVERY, hat: ['walk', 'bike'] },
  nat_ringlets: { ...EVERY, hat: ['walk', 'pose', 'bike'] },
  nat_half_up: { ...EVERY, hat: ['walk', 'pose', 'bike'] },
  nat_ponytail: { ...EVERY, right: true, hat: ['walk', 'pose', 'bike'] },   // it hangs on her left; tied low under a hat
  ken_buzz: EVERY,
  ken_fringe: { ...EVERY, hat: ['bike'] },
  ken_spiky: { ...EVERY, hat: ['bike'] },
};

// Buzz's head top is lower than today's curls by this much from each side (walk-cell px, measured on the art): the cap
// and the beanie painted on the curls floated over his close-cut head, the ground showing beside his temples (gate 4b)
const BUZZ_DOWN = { side: -16, front: -16, back: -8, front34: -15, back34: -5 };

/** {style: {itemId: 'hat' | {up}}}: an item that is not a hat but squashes a style draws its under-a-hat variant too
 *  ('hat'); one painted on today's hair that sits too low or high on a style draws `up` walk-cell px higher (itemLift):
 *  one number, or one per view ({side, front, back, front34, back34}) where the head differs by side. */
export const ITEM_FIT = {
  // the bows tie today's pigtails: on loose hair they hung halfway down it from behind (owner, gate 4a)
  nat_long_sleek: { akc_kokarda: { up: 20 } },
  nat_rounded_bob: { akc_kokarda: { up: 20 } },
  nat_wavy_lob: { akc_kokarda: { up: 20 } },    // batch 2: at today's height they sat at her jaw
  nat_half_up: { akc_kokarda: { up: 20 } },
  nat_ringlets: { akc_kokarda: { up: 20 } },
  nat_ponytail: { akc_kokarda: { up: 20 } },
  ken_buzz: { ken_akc_cap: { up: BUZZ_DOWN }, ken_akc_beanie: { up: BUZZ_DOWN } },
};

// The items a style's tail hangs over: seen from the side or from behind they draw from a copy of their walk sheet with
// the tail cut out (`<sheet>__<style>`, scripts/anim/hair/tails.py; gate 4b: "can we make the headband under the pony tail?")
export const ITEM_CUT = { nat_ponytail: new Set(['nat_headband']) };

/** The hats whose art carries today's hair under the brim (Ken's curls, her pigtails): over any other style they draw
 *  a copy with it cut out, '<key>__bare' (gate 4b round 2: "Ken · Buzz · cap standing diagonally looks wrong";
 *  scripts/anim/hair/bare.py). The straw hat is straw-blond like the hair and the crown carries none: as drawn. */
export const BARE = new Set(['ken_akc_cap', 'ken_akc_beanie', 'nat_bucket_hat']);

/** The walk sheet an item draws from on this look: its own (`key`), the copy cut for the style's tail (ITEM_CUT), or
 *  over a style that is not today's, a hat's bare copy (BARE). */
export function cutSheet(key, character, look, itemId, cut = ITEM_CUT) {
  const style = hairStyle(character, look);
  if (cut[style]?.has(itemId)) return `${key}__${style}`;
  return BARE.has(itemId) && style !== defaultStyle(character) ? `${key}__bare` : key;
}

/** Bike canvas px per walk-cell px (Natalka's head: 93 px wide on the bike, 117 in a walk cell). */
const BIKE_PER_WALK = 0.8;

/** How many px higher `itemId` draws over the look's style in `family` ('walk' | 'bike'; in the family's own art px)
 *  seen from `view` ('side' | 'front' | 'back' | 'front34' | 'back34'; a per-view fit with none: 0). Sitting and lying
 *  she faces the camera, where the items sit as painted. */
export function itemLift(family, character, look, itemId, fit = ITEM_FIT, view = null) {
  if (bodyOf(character) !== character || !['walk', 'bike'].includes(family)) return 0;
  const fitted = fit[hairStyle(character, look)]?.[itemId]?.up ?? 0;
  const up = typeof fitted === 'number' ? fitted : (fitted[view] ?? 0);
  return family === 'bike' ? Math.round(up * BIKE_PER_WALK) : up;
}

const bodyOf = (character) => (LOOK_CATALOG.defaults[character] ? character : 'natalka');
const defaultStyle = (character) => LOOK_CATALOG.defaults[bodyOf(character)].hairstyle;

/** The style the look asks for; an unknown id, or another body's, is the body's default. */
export const hairStyle = (character, look) => resolveLook(character, look).hairstyle;

function names(family, character, variant, neck) {
  const c = bodyOf(character);
  if (family === 'bike') {
    const atlas = `bike_hair_${c}_${variant}`;
    return { front: atlas, back: atlas, neck: neck ? atlas : null };
  }
  const kind = family === 'pose' ? 'pose' : 'walk';           // the portrait parts carry the walk sheets' names
  // another style walks with its own ears (gate 4b, 2026-11-01): the ones its editor drew, whole, a skin picture of
  // their own (scripts/anim/hair/ears.py drawn_whole: in the hair layer a hair colour painted them)
  const under = neck ? `${c}_${kind}_neck` : kind === 'walk' ? `${c}_walk_ears_${variant}` : null;
  return { front: `${c}_${kind}_hair_${variant}`, back: `${c}_${kind}_hairback_${variant}`, neck: under };
}

/**
 * {front, back, neck} atlas keys for a family ('walk' | 'pose' | 'bike' | 'portrait', which reads the walk's). A family
 * the style has no art for draws the default style; the hat variant only for a non-default style that has one in that
 * family, under a covering item or one ITEM_FIT says squashes it. `neck` is the skin layer under the clothes: today's
 * neck with the default style, its own ears with any other when it walks, else null.
 */
export function hairKeys(family, character, look, equipped, art = HAIR_ART, fit = ITEM_FIT) {
  const fallback = defaultStyle(character);
  const f = family === 'portrait' ? 'walk' : family;
  let style = hairStyle(character, look);
  if (!art[style]?.[f]) style = fallback;
  const item = equipped?.akcesorium;
  const hat = style !== fallback && (art[style]?.hat ?? []).includes(f) && (HEAD_COVER.has(item) || fit[style]?.[item] === 'hat');
  return names(family, character, hat ? `${style}_hat` : style, style === fallback);
}

/** The default style's walk keys: what a style sheet that fails to load is replaced with. */
export const hairFallback = (character) => names('walk', character, defaultStyle(character), true);

const sheetsOf = (k) => [k.front, k.back, k.neck].filter(Boolean);

/** Load a style's sheets (`load(keys, cb)`, `has(key)`; the default's neck with them), else the default's: cb gets the
 *  keys all of whose sheets loaded, or null when not even the default's did (the caller draws the flat art, whose hair
 *  is painted in). */
export function loadHair(load, has, keys, fallback, cb) {
  const all = (k) => sheetsOf(k).every(has);
  load(sheetsOf(keys), () => {
    if (all(keys)) { cb(keys); return; }
    if (fallback.front === keys.front) { cb(null); return; }
    load(sheetsOf(fallback), () => cb(all(fallback) ? fallback : null));
  });
}

/** Every style's own car picture, `driver_<c>_<style>` and a one-sided style's `…_r` (the asset manifest lists them;
 *  a style id starts with its body's prefix, 'nat_' or 'ken_'). The default styles draw today's `driver_<c>`. */
export function driverPictures(art = HAIR_ART) {
  const defaults = new Set(Object.values(LOOK_CATALOG.defaults).map((d) => d.hairstyle));
  return Object.entries(art).filter(([s, a]) => a.driver && !defaults.has(s)).flatMap(([s, a]) => {
    const key = `driver_${s.startsWith('ken_') ? 'ken' : 'natalka'}_${s}`;
    return a.right ? [key, `${key}_r`] : [key];
  });
}

/** The car window's picture: today's `driver_<c>` (the default style, baked in) or the style's own when it has driver
 *  art. A style without one draws the default rather than nobody. In a car facing right (`right`) a one-sided style's
 *  own `…_r`, stored mirrored back: the car flips its crew, so it shows facing right. */
export function driverKey(character, look, art = HAIR_ART, right = false) {
  const style = hairStyle(character, look);
  const c = bodyOf(character);
  if (style === defaultStyle(character) || !art[style]?.driver) return `driver_${c}`;
  return right && art[style].right ? `driver_${c}_${style}_r` : `driver_${c}_${style}`;
}
